import { Fragment, type ReactNode } from 'react';

/**
 * AOZONE-32: a deliberately tiny markup renderer for MBR drafts. We do NOT
 * pull in react-markdown — the draft surface only needs **bold**, Slack
 * <@user> mentions, and preserved newlines. Everything else (including
 * dangerous HTML) is rendered as inert text via React's escaping; we never
 * use dangerouslySetInnerHTML.
 */
export type DraftToken =
  | { type: 'text'; value: string }
  | { type: 'bold'; value: string }
  | { type: 'mention'; value: string };

const MENTION_RE = /<@([A-Za-z0-9._-]+)>/g;
const BOLD_RE = /\*\*([\s\S]+?)\*\*/g;

/** Inline tokens within a single text run: plain text + mention chips. */
type InlineToken = { type: 'text'; value: string } | { type: 'mention'; value: string };

/**
 * Split a text run on `<@user>` mentions, emitting text + mention tokens. A
 * bare `<@...>` never leaks because matches are replaced by mention tokens.
 */
function tokenizeInline(text: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  let lastIndex = 0;
  MENTION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > lastIndex) tokens.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    tokens.push({ type: 'mention', value: m[1] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) tokens.push({ type: 'text', value: text.slice(lastIndex) });
  return tokens;
}

/**
 * Tokenize a draft string. Bold is parsed FIRST so a `**bold**` span can
 * contain mentions (the house-style lead is `**<@U…> : Customer -**`); the
 * bold token carries its raw inner text, which the renderer re-tokenizes for
 * mention chips. Outside bold, mentions become chips and text stays text.
 * An unterminated `**` is left as literal text. Empty string → no tokens.
 */
export function tokenizeDraft(md: string): DraftToken[] {
  if (md === '') return [];
  const tokens: DraftToken[] = [];
  let lastIndex = 0;
  BOLD_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = BOLD_RE.exec(md)) !== null) {
    if (m.index > lastIndex) tokens.push(...tokenizeInline(md.slice(lastIndex, m.index)));
    tokens.push({ type: 'bold', value: m[1] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < md.length) tokens.push(...tokenizeInline(md.slice(lastIndex)));
  return tokens;
}

/** Render a text value, converting newlines to <br/> so lines stay separate. */
function renderText(value: string, keyBase: string): ReactNode {
  const parts = value.split('\n');
  return parts.map((part, i) => (
    <Fragment key={`${keyBase}-${i}`}>
      {i > 0 && <br />}
      {part}
    </Fragment>
  ));
}

/** A styled Slack-mention chip (ID-based for v1). */
function MentionChip({ id }: { id: string }) {
  return (
    <span
      data-mention={id}
      className="inline-flex items-center rounded bg-slate-100 px-1 text-xs font-medium text-slate-700"
    >
      @{id}
    </span>
  );
}

/**
 * Render an inline text run, turning `<@user>` tokens into mention chips and
 * preserving newlines. Used both at top level and inside bold spans.
 */
function renderInline(value: string, keyBase: string): ReactNode {
  return tokenizeInline(value).map((t, i) =>
    t.type === 'mention' ? (
      <MentionChip key={`${keyBase}-${i}`} id={t.value} />
    ) : (
      <Fragment key={`${keyBase}-${i}`}>{renderText(t.value, `${keyBase}-${i}`)}</Fragment>
    ),
  );
}

/**
 * Render the lightweight draft markup to React nodes. Newlines become <br/>
 * and the container also carries whitespace-pre-wrap for belt-and-braces.
 */
export function DraftPreview({ md }: { md: string }) {
  const tokens = tokenizeDraft(md);
  return (
    <div className="whitespace-pre-wrap text-sm text-slate-700">
      {tokens.map((t, i) => {
        if (t.type === 'bold') return <strong key={i}>{renderInline(t.value, `b${i}`)}</strong>;
        if (t.type === 'mention') return <MentionChip key={i} id={t.value} />;
        return <Fragment key={i}>{renderText(t.value, `t${i}`)}</Fragment>;
      })}
    </div>
  );
}
